import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import PencilIcon from './PencilIcon';
import './ScanCardTitle.scss';

const MAX_NAME_LENGTH = 50;

/**
 * ScanCardTitle – displays the scan name with an inline edit mode.
 *
 * Normal mode: shows the scan name (truncated with ellipsis) and a pencil icon button.
 * Edit mode: shows a text input pre-filled with the current name.
 *   - Enter  → saves the new name (calls onRename) and exits edit mode
 *   - Escape → cancels, restoring the original name
 *   - Blur   → cancels, restoring the original name
 *
 * @param {Object}   props
 * @param {string}   props.name      – Current scan name to display / edit
 * @param {Function} [props.onRename] – Called with the trimmed new name when saved
 */
const ScanCardTitle = ({ name, onRename }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(name);
  const inputRef = useRef(null);

  // Keep local name in sync if the prop changes externally (e.g. Redux rollback)
  useEffect(() => {
    if (!isEditing) {
      setInputValue(name);
    }
  }, [name, isEditing]);

  // Auto-focus and select all text when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const enterEditMode = useCallback((e) => {
    e.stopPropagation();
    setIsEditing(true);
  }, []);

  const save = useCallback(() => {
    const trimmed = inputValue.trim();
    if (trimmed && trimmed !== name) {
      onRename?.(trimmed);
    }
    setIsEditing(false);
  }, [inputValue, name, onRename]);

  const cancel = useCallback(() => {
    setInputValue(name);
    setIsEditing(false);
  }, [name]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        save();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      }
    },
    [save, cancel]
  );

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        className="scan-card-title-input"
        value={inputValue}
        maxLength={MAX_NAME_LENGTH}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={cancel}
        onClick={(e) => e.stopPropagation()}
        aria-label="Edit scan name"
      />
    );
  }

  return (
    <>
      <span className="scan-card-base-title">{name}</span>
      <button
        type="button"
        className="scan-card-title-edit-btn"
        onClick={enterEditMode}
        aria-label="Edit scan name"
      >
        <PencilIcon />
      </button>
    </>
  );
};

ScanCardTitle.propTypes = {
  name: PropTypes.string.isRequired,
  onRename: PropTypes.func
};

export default ScanCardTitle;
